import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button } from '../ui/button';
import { Loader2, CreditCard, ArrowLeft, ShieldCheck, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cache the loadStripe promise per publishable key so we don't re-init the SDK
// every time the form mounts.
const _stripeCache = {};
const getStripe = (key) => {
  if (!_stripeCache[key]) _stripeCache[key] = loadStripe(key);
  return _stripeCache[key];
};

const appearance = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#FF3B6B',
    colorText: '#1E1E24',
    borderRadius: '12px',
    fontFamily: 'inherit',
  },
};

const InnerCardForm = ({ bookingId, amount, paymentType, isOneTime, sendEmail, sendSms, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);

  const handlePay = async () => {
    if (!stripe || !elements) return;
    setBusy(true);
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });
      if (error) {
        toast.error(error.message || 'Your card could not be charged.');
        setBusy(false);
        return;
      }
      if (paymentIntent && paymentIntent.status === 'succeeded') {
        const res = await axios.post(
          `${API_URL}/api/payments/confirm-intent`,
          {
            payment_intent_id: paymentIntent.id,
            booking_id: bookingId || null,
            payment_type: paymentType || null,
            is_one_time: isOneTime ?? null,
            send_email: sendEmail ?? null,
            send_sms: sendSms ?? null,
          },
          { withCredentials: true },
        );
        onSuccess?.(res.data);
        return;
      }
      toast.error(`Payment ${paymentIntent?.status || 'was not completed'}.`);
      setBusy(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Payment failed. Please try again.');
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="stripe-card-form">
      <PaymentElement onReady={() => setReady(true)} options={{ layout: 'tabs' }} />
      <Button
        onClick={handlePay}
        disabled={!stripe || !ready || busy}
        className="w-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full py-6 text-base font-semibold"
        data-testid="stripe-pay-btn"
      >
        {busy ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing…</>
        ) : (
          <><CreditCard className="w-4 h-4 mr-2" /> Pay ${Number(amount).toFixed(2)} CAD</>
        )}
      </Button>
      {onCancel && (
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="w-full text-xs text-[#6B6B7A] hover:text-[#FF3B6B] flex items-center justify-center gap-1 disabled:opacity-40"
          data-testid="stripe-card-back"
        >
          <ArrowLeft className="w-3 h-3" /> Back
        </button>
      )}
      <div className="flex items-center justify-center gap-1.5 text-[11px] text-[#6B6B7A]">
        <ShieldCheck className="w-3.5 h-3.5 text-[#06D6A0]" />
        Secured by Stripe · your card details never touch our servers
      </div>
    </div>
  );
};

/**
 * StripeCardForm — self-contained Stripe Elements card entry.
 *
 * On mount it creates a PaymentIntent (POST /api/payments/create-intent) with
 * the supplied amount/note/pin, then renders the PaymentElement. Confirming
 * the card keeps the user on the page (no redirect) and reports back the
 * completed payment via onSuccess.
 *
 * Props:
 *   context   'admin' | 'customer'  (admin requires a staff PIN server-side)
 *   bookingId optional booking to attach the payment to
 *   amount    number (CAD)
 *   note      optional internal note (admin)
 *   pin       staff PIN (admin only)
 *   onSuccess (result) => void  — result: { ok, amount, fully_paid, new_outstanding }
 *   onCancel  () => void
 */
export const StripeCardForm = ({ context = 'customer', bookingId, amount, note, pin, paymentType, customerEmail, customerName, customerPhone, customerId, isOneTime, sendEmail = true, sendSms = true, onSuccess, onCancel }) => {
  const [clientSecret, setClientSecret] = useState(null);
  const [pubKey, setPubKey] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .post(
        `${API_URL}/api/payments/create-intent`,
        {
          context,
          booking_id: bookingId || null,
          amount: Number(amount),
          note: note || null,
          pin: pin || null,
          payment_type: paymentType || null,
          customer_email: customerEmail || null,
          customer_name: customerName || null,
          customer_phone: customerPhone || null,
          customer_id: customerId || null,
          is_one_time: !!isOneTime,
          send_email: sendEmail,
          send_sms: sendSms,
        },
        { withCredentials: true },
      )
      .then((res) => {
        if (cancelled) return;
        setClientSecret(res.data.client_secret);
        setPubKey(res.data.publishable_key);
      })
      .catch((e) => {
        if (!cancelled) {
          const msg = e.response?.data?.detail || 'Could not start the card payment.';
          setError(msg);
          toast.error(msg);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stripePromise = useMemo(() => (pubKey ? getStripe(pubKey) : null), [pubKey]);

  if (loading) {
    return (
      <div className="py-10 flex flex-col items-center gap-3 text-sm text-[#6B6B7A]" data-testid="stripe-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#FF3B6B]" />
        Preparing secure card form…
      </div>
    );
  }
  if (error) {
    return (
      <div className="py-6 text-center space-y-3" data-testid="stripe-error">
        <div className="flex items-center justify-center gap-2 text-sm text-[#E5484D]">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
        {onCancel && (
          <Button variant="outline" onClick={onCancel} className="rounded-full" data-testid="stripe-error-back">
            Back
          </Button>
        )}
      </div>
    );
  }
  if (!clientSecret || !stripePromise) return null;

  return (
    <Elements stripe={stripePromise} options={{ clientSecret, appearance }}>
      <InnerCardForm bookingId={bookingId} amount={amount} paymentType={paymentType} isOneTime={isOneTime} sendEmail={sendEmail} sendSms={sendSms} onSuccess={onSuccess} onCancel={onCancel} />
    </Elements>
  );
};

export default StripeCardForm;
